import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';

interface SubscriptionInfo {
  tier?: string;
  active?: boolean;
  expiresAt?: string | null;
}

const SubscriptionSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo>({});
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptionInfo = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out')), 10000);
      });
      
      const fetchPromise = supabase.functions.invoke('get-subscription-status');
      const result = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (result.error) throw new Error(result.error.message);
      
      // Provide default values if data is incomplete
      setSubscriptionInfo({
        tier: result.data?.tier || 'free',
        active: result.data?.hasValidSubscription || false,
        expiresAt: result.data?.expiresAt
      });
      
      console.log('Subscription info loaded:', result.data);
    } catch (err: any) {
      console.error('Error fetching subscription info:', err);
      setError(err.message || 'Failed to load subscription data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchSubscriptionInfo();
    }
  }, [user?.id]);

  // Function to format the subscription end date
  const formatExpiryDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Stripe customer portal URL
  const stripePortalUrl = "https://billing.stripe.com/p/login/4gwfZpeUZ9pm0SYdQQ";

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-center items-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>Subscription Plan</CardTitle>
          <CardDescription>
            Manage your subscription plan and billing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <div className="text-center">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={fetchSubscriptionInfo}>
                Retry
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <p className="text-sm font-medium">Current Plan</p>
                <p className="text-2xl font-bold capitalize">
                  {subscriptionInfo.tier || 'Free'} Plan
                </p>
                <p className={`text-sm ${subscriptionInfo.active ? 'text-green-500' : 'text-amber-500'}`}>
                  {subscriptionInfo.active ? 'Active' : 'Inactive'}
                </p>
              </div>

              {subscriptionInfo.expiresAt && (
                <div className="space-y-1">
                  <p className="text-sm font-medium">Renews On</p>
                  <p>{formatExpiryDate(subscriptionInfo.expiresAt)}</p>
                </div>
              )}
            </>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          {subscriptionInfo.active ? (
            <>
              <Button
                className="w-full"
                onClick={() => window.open(stripePortalUrl, '_blank')}
              >
                Manage Subscription
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                You'll be redirected to the Stripe Customer Portal to manage your subscription plan, payment methods, and billing information.
              </p>
            </>
          ) : (
            <>
              <Button
                className="w-full"
                onClick={() => navigate('/pricing')}
              >
                Subscribe to Pro
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Get expert fantasy football insights with a 7-day free trial.
              </p>
            </>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default SubscriptionSection;
